import { useGetHubspotPropertiesQuery } from "@api/integrations";
import { IconBuilding, IconSignature, IconUserDown } from "@tabler/icons-react";
import { useMemo } from "react";
import type { PickerCategory, PickerVariable } from "../types";

type HubspotProperty = {
  name: string;
  label: string;
  type: string;
  fieldType: string;
  groupName: string;
};

// Three static HubSpot categories → CRM object types (plural for the endpoint).
export const HUBSPOT_CATEGORY_DEFS = [
  {
    id: "contact",
    label: "Contact",
    objectType: "contacts",
    icon: IconUserDown,
  },
  { id: "deal", label: "Deal", objectType: "deals", icon: IconSignature },
  {
    id: "company",
    label: "Company",
    objectType: "companies",
    icon: IconBuilding,
  },
] as const;

const toVariables = (
  categoryId: string,
  properties: HubspotProperty[] | undefined,
): PickerVariable[] =>
  (properties ?? []).map((prop) => ({
    id: `hubspot.${categoryId}.${prop.name}`,
    name: prop.label,
    token: `{{hubspot.${categoryId}.${prop.name}}}`,
    type: "hubspot",
  }));

/**
 * Live HubSpot categories. All three object types' properties are fetched
 * upfront (when `enabled`) so each category row can show its property count;
 * RTK Query caches per objectType, so the hover flyout reuses the same data.
 */
export const useHubspotCategories = (
  enabled: boolean,
): { categories: PickerCategory[] } => {
  const contact = useGetHubspotPropertiesQuery(
    { objectType: "contacts" },
    { skip: !enabled },
  );
  const deal = useGetHubspotPropertiesQuery(
    { objectType: "deals" },
    { skip: !enabled },
  );
  const company = useGetHubspotPropertiesQuery(
    { objectType: "companies" },
    { skip: !enabled },
  );

  const categories = useMemo(() => {
    const byType = {
      contacts: contact,
      deals: deal,
      companies: company,
    } as const;
    return HUBSPOT_CATEGORY_DEFS.map((def) => {
      const query = byType[def.objectType];
      return {
        id: def.id,
        label: def.label,
        icon: def.icon,
        isHubspot: true,
        isLoading: query.isLoading || query.isFetching,
        variables: toVariables(def.id, query.data),
      } satisfies PickerCategory;
    });
    // Depend on each query's data/loading so counts update as they resolve.
  }, [contact, deal, company]);

  return { categories };
};
